import Button from 'tio-reimagined/components/button';
import { dropTask } from 'ember-concurrency';
import { eq, not } from 'tio-ui/utilities';
import { fn } from '@ember/helper';
import Modal from 'tio-reimagined/components/modal';
import Footer from 'tio-reimagined/components/footer';
import { get } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { Popover } from 'tio-ui/components/popover';
import { Section } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { Switch } from 'tio-ui/components/forms';
import Textarea from 'tio-reimagined/components/textarea';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import CopyButton from '../ui/copy-button.gts';
import Table from 'tio-ui/components/table';
import type EmployeeModel from '../../models/employee.ts';
import type IntlService from 'ember-intl/services/intl';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type TasEligibilityModel from '../../models/tas-eligibility.ts';

interface EmployeeEligibilityComponentSignature {
  Args: {
    employee: EmployeeModel;
    isAdminView?: boolean;
  };
}

class EmployeeEligibility extends Component<EmployeeEligibilityComponentSignature> {
  @service declare store: StoreService;
  @service declare intl: IntlService;

  @tracked holdParticipantModal = false;
  @tracked eligibilityStrategyModal = false;
  @tracked eligibilityStrategyComment = '';
  @tracked updateTasEligibilityModal = false;
  @tracked selectedTasEligibility: TasEligibilityModel | null = null;

  get employee() {
    return this.args.employee;
  }

  get participantId() {
    return this.employee.tasParticipant?.id;
  }

  get isInActive() {
    return this.employee?.tasParticipant?.state === 'TAS.ParticipantState.INACTIVE';
  }

  get tasParticipationStatus() {
    return this.employee?.tasParticipant?.state.split('.')[2];
  }

  get employeeEligibilities() {
    return this.employee.tasEligibilities;
  }

  dynamicEligibility = trackedFunction(this, async () => {
    const eligibility = await this.store
      .adapterFor('tas-participant')
      // @ts-expect-error - Legacy adapter method, will be migrated
      .dynamicEligibility(this.participantId);
    return [eligibility];
  });

  get employeeDynamicEligibility() {
    return this.dynamicEligibility.value || [];
  }

  toggleHoldParticipantModal = () => {
    this.holdParticipantModal = !this.holdParticipantModal;
  };

  toggleEligibilityStrategyModal = () => {
    this.eligibilityStrategyComment = '';
    this.eligibilityStrategyModal = !this.eligibilityStrategyModal;
  };

  toggleUpdateTasEligibilityModal = (eligibility?: TasEligibilityModel) => {
    this.selectedTasEligibility = eligibility || null;
    this.updateTasEligibilityModal = !this.updateTasEligibilityModal;
  };

  get isActiveParticipant() {
    return this.tasParticipationStatus === 'ACTIVE';
  }

  get holdChangeNote() {
    return `Hold/Reactivate employee: ${this.employee.fullName}`;
  }

  toggleHold = dropTask(async () => {
    if (this.isActiveParticipant) {
      await this.store
        .adapterFor('tas-participant')
        // @ts-expect-error - Legacy adapter method, will be migrated
        .holdTasParticipant(this.participantId, { changelog: this.holdChangeNote });
    } else {
      await this.store
        .adapterFor('tas-participant')
        // @ts-expect-error - Legacy adapter method, will be migrated
        .removeTasParticipantHold(this.participantId, { changelog: this.holdChangeNote });
    }
    await this.employee.tasParticipant.reload();
    this.toggleHoldParticipantModal();
  });

  get eligibilityStrategy() {
    if (
      this.employee.tasParticipant?.eligibilityStrategy ===
      'TAS.EligibilityStrategy.ELIGIBILITY_SERVICE'
    ) {
      return 'Auto';
    }
    return 'Manual';
  }

  updateTasEligibilityState = dropTask(async () => {
    if (!this.selectedTasEligibility) {
      return;
    }
    const newEligibilityState = this.selectedTasEligibility.isActive ? 'deactivate' : 'activate';

    await this.store
      .adapterFor('tas-eligibility')
      // @ts-expect-error - Legacy adapter method, will be migrated
      .updateTasEligibilityState(this.selectedTasEligibility.id, newEligibilityState);

    await this.selectedTasEligibility.reload();
    this.toggleUpdateTasEligibilityModal();
  });

  setEligibilityStrategyComment = (value: string) => {
    this.eligibilityStrategyComment = value;
  };

  toggleEligibilityStrategy = dropTask(async () => {
    const newEligibilityStrategy =
      this.eligibilityStrategy === 'Auto' ? 'manual-intervention' : 'eligibility-service';
    await this.store
      .adapterFor('tas-participant')
      // @ts-expect-error - Legacy adapter method, will be migrated
      .updateEligibilityStrategy(this.participantId, newEligibilityStrategy, {
        changelog: this.eligibilityStrategyComment,
      });
    await this.employee.tasParticipant.reload();
    this.toggleEligibilityStrategyModal();
  });

  get toggleEligibilityStrategyDisclaimer() {
    const translationKey =
      this.eligibilityStrategy === 'Auto'
        ? 'common.tuition_assistance.employee.manual_program_eligibility_warning'
        : 'common.tuition_assistance.employee.auto_program_eligibility_warning';

    return this.intl.t(translationKey);
  }

  get updateProgramEligibilityDisclaimer() {
    if (!this.selectedTasEligibility) {
      return '';
    }
    const translationKey = this.selectedTasEligibility.isActive
      ? 'common.tuition_assistance.employee.deactivate_eligibility_disclaimer'
      : 'common.tuition_assistance.employee.activate_eligibility_disclaimer';
    return this.intl.t(translationKey, {
      program: this.selectedTasEligibility.tasProgramTemplate.fields.PROGRAM_NAME.values[0],
    });
  }

  formatExpression(raw: string): string {
    return raw
      .replace(/\s+AND\s+/gi, '\nAND ')
      .replace(/IF\s*\(\s*/gi, 'IF(\n  ')
      .replace(/\)\s*AND/gi, '\n)\nAND')
      .replace(/IN\s*\(\s*/gi, 'IN (\n  ')
      .replace(/\)\s*$/m, '\n)')
      .replace(/\n{2,}/g, '\n\n');
  }

  eligibilityActiveOrInactive = (code: string, isActive: boolean) => {
    const dynamicMapping = this.employeeDynamicEligibility?.[0] ?? {};
    const dynamicCode = dynamicMapping[code];

    if (dynamicCode === true) return 'ACTIVE';
    if (dynamicCode === false) return 'INACTIVE';
    return isActive ? 'ACTIVE' : 'INACTIVE';
  };

  <template>
    <Section id="participation-status">
      <:header>{{t "common.tuition_assistance.employee.participation_status"}}</:header>
      <:actions>
        {{#if this.isInActive}}
          {{t "common.inactive"}}
        {{else}}
          <div class="flex gap-2">
            <label>
              {{t "common.tuition_assistance.employee.held"}}
            </label>
            <Switch
              @label={{t "common.tuition_assistance.employee.active"}}
              @intent="success"
              @isSelected={{this.isActiveParticipant}}
              @onChange={{this.toggleHoldParticipantModal}}
            />
          </div>
        {{/if}}
      </:actions>
      <:body>
        <div class="overflow-x-auto">
          <div class="flex justify-between">
            <span>{{t "common.tuition_assistance.employee.program_eligibility"}}</span>
            <div class="flex gap-2">
              <label>
                Auto
              </label>
              <Switch
                @label="Manual"
                @intent="success"
                @isSelected={{eq this.eligibilityStrategy "Manual"}}
                @isDisabled={{not this.isActiveParticipant}}
                @onChange={{this.toggleEligibilityStrategyModal}}
              />
            </div>
          </div>
          <Table @isLoading={{false}}>
            <:thead as |options|>
              <options.tr>
                <options.th>{{t "common.tuition_assistance.employee.program_code"}}</options.th>
                <options.th>{{t "common.tuition_assistance.employee.program_name"}}</options.th>
                <options.th>
                  {{t "common.tuition_assistance.employee.eligibility_state"}}</options.th>
                <options.th>{{t "common.tuition_assistance.employee.expression"}}</options.th>
              </options.tr>
            </:thead>
            <:tbody as |options|>
              {{#unless this.employeeEligibilities.length}}
                <options.tr>
                  <options.td colspan="5">
                    {{t "common.no_records_found"}}
                  </options.td>
                </options.tr>
              {{/unless}}
              {{#each this.employeeEligibilities as |eligibility|}}
                {{#if
                  (eq eligibility.tasProgramTemplate.state "TAS.ProgramTemplateState.LAUNCHED")
                }}
                  <options.tr>
                    <options.td>
                      {{#if @isAdminView}}
                        <LinkTo
                          @route="authenticated.tuition-assistance.program-builder.program-template"
                          @model={{eligibility.tasProgramTemplate.id}}
                          class="tio-anchor"
                        >
                          {{eligibility.tasProgramTemplate.code}}
                        </LinkTo>
                      {{else}}
                        {{eligibility.tasProgramTemplate.code}}
                      {{/if}}
                    </options.td>
                    <options.td>{{get
                        eligibility.tasProgramTemplate.fields.PROGRAM_NAME.values
                        "0"
                      }}</options.td>
                    <options.td>
                      {{#if (eq this.eligibilityStrategy "Auto")}}
                        {{this.eligibilityActiveOrInactive
                          eligibility.tasProgramTemplate.code
                          eligibility.isActive
                        }}
                      {{else}}
                        <div class="flex gap-2">
                          <label>{{t "common.inactive"}}</label>
                          <Switch
                            @label={{t "common.tuition_assistance.employee.active"}}
                            @intent="success"
                            @isSelected={{eligibility.isActive}}
                            @onChange={{fn this.toggleUpdateTasEligibilityModal eligibility}}
                          />
                        </div>
                      {{/if}}
                    </options.td>
                    <options.td>
                      {{#if eligibility.tasProgramTemplate.eligibilityExpression}}
                        <div class="flex gap-x-4">
                          <Popover @placement="left" as |pop|>
                            <Button @intent="primary" {{pop.trigger}} {{pop.anchor}}>
                              View
                            </Button>
                            <pop.Content
                              @blockScroll={{false}}
                              @backdrop="faded"
                              @size="xl"
                              @class="p-4"
                            >
                              <pre
                                class="text-tio-gray-700 text-pretty leading-relaxed text-sm font-mono whitespace-pre-wrap overflow-x-hidden break-words text-balance"
                              >
                                {{~this.formatExpression
                                  eligibility.tasProgramTemplate.eligibilityExpression
                                }}
                              </pre>
                            </pop.Content>
                          </Popover>
                          <CopyButton
                            @text={{eligibility.tasProgramTemplate.eligibilityExpression}}
                            @title="Copy Expression"
                          />
                        </div>
                      {{/if}}
                    </options.td>
                  </options.tr>
                {{/if}}
              {{/each}}
            </:tbody>
          </Table>
        </div>
      </:body>
    </Section>
    <Modal
      @isOpen={{this.holdParticipantModal}}
      @onClose={{this.toggleHoldParticipantModal}}
      class="!max-w-screen-sm"
      @ariaLabel={{t "common.aria_labels.participation_status"}}
    >
      <:header>{{t "common.tuition_assistance.employee.participation_status"}}</:header>
      <:body>
        <p class="mb-4">{{t "common.tuition_assistance.employee.participation_status_warning"}}</p>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.toggleHold.isRunning}}
          @onCancel={{this.toggleHoldParticipantModal}}
          @onSubmit={{this.toggleHold.perform}}
          @submitText={{t "common.confirm"}}
        />
      </:footer>
    </Modal>
    <Modal
      @isOpen={{this.eligibilityStrategyModal}}
      @onClose={{this.toggleEligibilityStrategyModal}}
      class="!max-w-screen-sm"
      @ariaLabel={{t "common.aria_labels.program_eligibility"}}
    >
      <:header>{{t "common.tuition_assistance.employee.program_eligibility"}}</:header>
      <:body>
        <p class="mb-4">{{this.toggleEligibilityStrategyDisclaimer}}</p>
        <Textarea
          @label={{t "common.comment"}}
          @value={{this.eligibilityStrategyComment}}
          @onChange={{this.setEligibilityStrategyComment}}
        />
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.toggleEligibilityStrategy.isRunning}}
          @onCancel={{this.toggleEligibilityStrategyModal}}
          @onSubmit={{this.toggleEligibilityStrategy.perform}}
          @submitDisabled={{eq this.eligibilityStrategyComment ""}}
          @submitText={{t "common.confirm"}}
        />
      </:footer>
    </Modal>
    <Modal
      @isOpen={{this.updateTasEligibilityModal}}
      @onClose={{this.toggleUpdateTasEligibilityModal}}
      class="!max-w-screen-sm"
      @ariaLabel={{t "common.aria_labels.update_eligibility"}}
    >
      <:header>{{t "common.tuition_assistance.employee.update_eligibility"}}</:header>
      <:body>
        <p class="mb-4">{{this.updateProgramEligibilityDisclaimer}}</p>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.updateTasEligibilityState.isRunning}}
          @onCancel={{this.toggleUpdateTasEligibilityModal}}
          @onSubmit={{this.updateTasEligibilityState.perform}}
          @submitText={{t "common.confirm"}}
        />
      </:footer>
    </Modal>
  </template>
}

export default EmployeeEligibility;
